import { Button, Divider, Flex, Paper, Stack } from "@mantine/core";
import { salesOrderApi } from "apis/salesOrderApi";
import { Loading } from "components";
import { SalesOrderCreateBody } from "constants/types/salesOrder";
import { FormikProvider, useFormik } from "formik";
import { FORM_IDS } from "pages/SalesOrder/config";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SALES_ORDER_PAGE } from "routes/route.constant";
import { useNotification } from "utils/hooks";
import { useListInventory } from "utils/hooks/request/useInventories";
import {
  SOCreateFormValues,
  createValidationSchema,
  initialValues,
} from "../../config";
import SalesOrderGeneralFields from "../SalesOrderGeneralFields/SalesOrderGeneralFields";
import SalesOrderItemsFieldArray from "../SalesOrderItemsFieldArray/SalesOrderItemsFieldArray";
import SalesOrderPartnerFields from "../SalesOrderPartnerFields/SalesOrderPartnerFields";

const SalesOrderCreateForm: FC = () => {
  const navigate = useNavigate();
  const { notification } = useNotification();
  const { inventories } = useListInventory();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik<SOCreateFormValues>({
    initialValues,
    validationSchema: createValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // id / regionId / contactPersonId là @IsUUID @IsOptional ở backend:
        // BỎ HẲN khi rỗng, không gửi chuỗi "" — gửi "" sẽ ăn 400
        // "must be a UUID" chứ không được coi là "bỏ trống".
        const {
          id,
          notes: partnerNotes,
          contactPersonId,
          regionId,
          ...partnerRest
        } = values.partner;

        const payload: SalesOrderCreateBody = {
          ...(values.code ? { code: values.code } : {}),
          warehouseId: values.warehouseId,
          notes: values.notes || undefined,
          createdAt: values.createdAt,
          // Cấp ĐƠN: backend lưu riêng trên đơn. Vẫn giữ trong khối partner bên
          // dưới để khi tạo khách MỚI thì hồ sơ khách cũng có người phụ trách.
          ...(contactPersonId ? { contactPersonId } : {}),
          partner: {
            ...(id ? { id } : {}),
            ...partnerRest,
            notes: partnerNotes || undefined,
            ...(regionId ? { regionId } : {}),
            ...(contactPersonId ? { contactPersonId } : {}),
          },
          items: values.items.map(
            ({ rowKey: _rowKey, productSpec, ...item }) => ({
              ...item,
              ...(productSpec ? { productSpec } : {}),
            }),
          ),
          ...(values.oldDebt ? { oldDebt: values.oldDebt } : {}),
        };

        const res = await salesOrderApi.create(payload);
        if (res.data.success) {
          notification.success({ title: "Tạo đơn bán hàng thành công" });
          navigate(SALES_ORDER_PAGE);
        } else {
          notification.error({ title: "Tạo đơn bán hàng thất bại" });
        }
      } catch (error: any) {
        notification.error({
          title: "Tạo đơn bán hàng thất bại",
          message: error?.error?.message,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Loading variant="overlay" visible={isLoading}>
      <FormikProvider value={formik}>
        <form id={FORM_IDS.CREATE} onSubmit={formik.handleSubmit}>
          <Stack gap="md">
            <Paper p="md" withBorder>
              <SalesOrderGeneralFields formik={formik} />
            </Paper>

            <Paper p="md" withBorder>
              <SalesOrderPartnerFields formik={formik} />
            </Paper>

            <Paper p="md" withBorder>
              <SalesOrderItemsFieldArray formik={formik} inventories={inventories} />
            </Paper>

            <Divider />

            <Flex justify={"center"}>
              <Button
                type="submit"
                form={FORM_IDS.CREATE}
                w={250}
                loading={isLoading}
              >
                Tạo đơn bán hàng
              </Button>
            </Flex>
          </Stack>
        </form>
      </FormikProvider>
    </Loading>
  );
};

export default SalesOrderCreateForm;
